import { z } from 'zod';

type ValueOf<T> = T[keyof T];

export const TeamColors = {
  BLUE: 'blue',
  RED: 'red',
} as const;
export type TeamColorsValues = ValueOf<typeof TeamColors>;

export const GuessModes = {
  LAST: 'last',
  TWO_LAST: 'lastTwo',
  CUSTOM: 'custom',
} as const;
export type GuessModesValues = ValueOf<typeof GuessModes>;

export const GameMode = {
  BOUNTY: 'bounty',
  BRAWL_BALL: 'brawlBall',
  GEM_GRAB: 'gemGrab',
  HEIST: 'heist',
  HOT_ZONE: 'hotZone',
  KNOCKOUT: 'knockout',
  //   WIPEOUT: 'wipeout',
} as const;
export type GameModeValues = ValueOf<typeof GameMode>;

export type Map = { id: number; name: string; mode: GameModeValues };

export const TeamBrawlerSchema = z.object({
  id: z.union([z.number(), z.null()]),
  ban: z.union([z.number(), z.null()]),
  starPower: z.union([z.number(), z.null()]),
  gadget: z.union([z.number(), z.null()]),
});
export type TeamBrawler = z.infer<typeof TeamSchema>;
export const TeamSchema = z.object({
  1: TeamBrawlerSchema,
  2: TeamBrawlerSchema,
  3: TeamBrawlerSchema,
});
export type Team = z.infer<typeof TeamSchema>;
export type TeamIdx = keyof Team;

export const SettingsSchema = z.object({
  coin: z.enum([TeamColors.BLUE, TeamColors.RED]),
  mode: z.enum([GuessModes.LAST, GuessModes.TWO_LAST, GuessModes.CUSTOM]),
  tries: z.number(),
});
export type Settings = z.infer<typeof SettingsSchema>;

export const StarPowerSchema = z.object({ id: z.number(), name: z.string() });
export type StarPower = z.infer<typeof StarPowerSchema>;

export const GadgetSchema = z.object({ id: z.number(), name: z.string() });
export type Gadget = z.infer<typeof GadgetSchema>;

export const BrawlerSchema = z.object({
  id: z.number(),
  name: z.string(),
  starPowers: StarPowerSchema.array(),
  gadgets: GadgetSchema.array(),
});
export type Brawler = z.infer<typeof BrawlerSchema>;

export const DraftSchema = z.object({
  blueTeam: TeamSchema,
  redTeam: TeamSchema,
  mapId: z.union([z.number(), z.null()]),
  settings: SettingsSchema,
});
export type Draft = z.infer<typeof DraftSchema>;

export type DraftPartial = Omit<Draft, 'settings'> & {
  settings: {
    coin: TeamColorsValues | null;
    mode: GuessModesValues | null;
    tries: number | null;
  };
};

// Complete
export const TeamBrawlerCompleteSchema = z.object({
  id: z.number(),
  ban: z.number(),
  starPower: z.number(),
  gadget: z.number(),
});
export type TeamBrawlerComplete = z.infer<typeof TeamBrawlerCompleteSchema>;
export const TeamCompleteSchema = z.object({
  1: TeamBrawlerCompleteSchema,
  2: TeamBrawlerCompleteSchema,
  3: TeamBrawlerCompleteSchema,
});
export type TeamComplete = z.infer<typeof TeamCompleteSchema>;
export const DraftCompleteSchema = z.object({
  blueTeam: TeamCompleteSchema,
  redTeam: TeamCompleteSchema,
  mapId: z.number(),
  settings: SettingsSchema,
});
export type DraftComplete = z.infer<typeof DraftCompleteSchema>;

// Compress
export const SettingsCompressSchema = z.object({
  c: z.enum(['b', 'r']),
  m: z.enum(['l', '2', 'c']),
  t: z.string(),
});
export type SettingsCompress = z.infer<typeof SettingsCompressSchema>;
export const TeamBrawlerCompressSchema = z.object({
  i: z.string(),
  b: z.string(),
  s: z.string(),
  g: z.string(),
});
export type TeamBrawlerCompress = z.infer<typeof TeamBrawlerCompressSchema>;

export const DraftCompressSchema = z.object({
  b: z.string(),
  r: z.string(),
  m: z.string(),
  s: z.string(),
});
export type DraftCompress = z.infer<typeof DraftCompressSchema>;

export type PickMode = 'brawler' | 'ban';
export type Pick = {
  color: TeamColorsValues;
  index: TeamIdx;
  mode: PickMode;
};

export type GuessDraftResponse = {
  win: boolean;
  blueTeam: Partial<TeamComplete>;
  redTeam: Partial<TeamComplete>;
};

export * from './helpers/array';
export * from './helpers/date';
export * from './helpers/http';
